import {
  IFetchVacationCarryOverRequestsFilterParams,
  IVacationCarryOverRequestPaginatedListData,
  IVacationCarryOverRequestService
} from './vacation-carry-over-request.types';
import { AxiosInstance } from 'axios';
import {
  IAttachmentData,
  IMentionAttachmentCommentData,
  IUserData,
  IVacationCarryOverRequestCommentData,
  IVacationCarryOverRequestData
} from '@/models';

import { IUpdateVacationCarryOverStatePayload } from '@/store/vacation-carry-over-request';
import { ITransition } from '@/models/transition';
import { objectToFormData } from '@/utils/objectToFormData';

export class VacationCarryOverRequestService
  implements IVacationCarryOverRequestService {
  private endpoint: string;

  constructor(private axios: AxiosInstance) {
    this.endpoint = 'v1/timeoffs/carry-over-requests/';
  }

  fetchVacationCarryOverRequests(
    param?: IFetchVacationCarryOverRequestsFilterParams
  ): Promise<IVacationCarryOverRequestPaginatedListData> {
    return this.axios
      .get<IVacationCarryOverRequestPaginatedListData>(`${this.endpoint}`, {
        params: {
          search: param?.search,
          page: param?.page,
          state: param?.state
        }
      })
      .then(({ data }) => data);
  }

  fetchVacationCarryOverRequest(
    id: number
  ): Promise<IVacationCarryOverRequestData> {
    return this.axios
      .get<IVacationCarryOverRequestData>(`${this.endpoint}${id}/`)
      .then(({ data }) => data);
  }

  createVacationCarryOverRequest(
    data: IVacationCarryOverRequestData
  ): Promise<IVacationCarryOverRequestData> {
    return this.axios
      .post<IVacationCarryOverRequestData>(`${this.endpoint}`, data)
      .then(({ data }) => data);
  }

  updateVacationCarryOverRequest(
    data: IVacationCarryOverRequestData
  ): Promise<IVacationCarryOverRequestData> {
    return this.axios
      .patch<IVacationCarryOverRequestData>(`${this.endpoint}${data.id}/`, data)
      .then(({ data }) => data);
  }

  deleteVacationCarryOverRequest(id: number): Promise<any> {
    return this.axios.delete(`${this.endpoint}${id}/`);
  }

  updateVacationCarryOverRequestState(
    data: IUpdateVacationCarryOverStatePayload
  ): Promise<IVacationCarryOverRequestData> {
    return this.axios
      .put<IVacationCarryOverRequestData>(
        `${this.endpoint}${data.id}/set_state/${data.state}/`,
        {
          assignee: data.assignee,
          comment: data.comment
        }
      )
      .then(({ data }) => data);
  }

  fetchVacationCarryOverRequestActivities(id: number): Promise<ITransition[]> {
    return this.axios
      .get<ITransition[]>(`${this.endpoint}${id}/activities/`)
      .then(({ data }) => data);
  }

  //comments
  fetchVacationCarryOverRequestComments(
    vacationCarryOverId: number
  ): Promise<IVacationCarryOverRequestCommentData[]> {
    return this.axios
      .get<IVacationCarryOverRequestCommentData[]>(
        `${this.endpoint}${vacationCarryOverId}/comments/`
      )
      .then(({ data }) => data);
  }

  fetchVacationCarryOverRequestComment(
    vacationCarryOverId: number,
    commentId: number
  ): Promise<IVacationCarryOverRequestCommentData> {
    return this.axios
      .get<IVacationCarryOverRequestCommentData>(
        `${this.endpoint}${vacationCarryOverId}/comments/${commentId}/`
      )
      .then(({ data }) => data);
  }

  createVacationCarryOverRequestComment(
    vacationCarryOverId: number,
    comment: IVacationCarryOverRequestCommentData
  ): Promise<IVacationCarryOverRequestCommentData> {
    return this.axios
      .post<IVacationCarryOverRequestCommentData>(
        `${this.endpoint}${vacationCarryOverId}/comments/`,
        comment
      )
      .then(({ data }) => data);
  }

  updateVacationCarryOverRequestComment(
    vacationCarryOverId: number,
    comment: IVacationCarryOverRequestCommentData
  ): Promise<IVacationCarryOverRequestCommentData> {
    return this.axios
      .patch<IVacationCarryOverRequestCommentData>(
        `${this.endpoint}${vacationCarryOverId}/comments/${comment.id}/`,
        comment
      )
      .then(({ data }) => data);
  }

  deleteVacationCarryOverRequestComment(
    vacationCarryOverId: number,
    commentId: number
  ): Promise<void> {
    return this.axios
      .delete(`${this.endpoint}${vacationCarryOverId}/comments/${commentId}/`)
      .then(({ data }) => data);
  }

  addVacationCarryOverRequestCommentAttachments(
    vacationCarryOverId: number,
    commentId: number,
    attachments: IAttachmentData[]
  ): Promise<IMentionAttachmentCommentData> {
    const formData = objectToFormData({ attachments });
    return this.axios
      .post<IMentionAttachmentCommentData>(
        `${this.endpoint}${vacationCarryOverId}/comments/${commentId}/add-attachments/`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      )
      .then(({ data }) => data);
  }

  removeVacationCarryOverRequestCommentAttachment(
    vacationCarryOverId: number,
    commentId: number,
    attachmentId: number
  ): Promise<void> {
    return this.axios
      .delete(
        `${this.endpoint}${vacationCarryOverId}/comments/${commentId}/remove-attachment/${attachmentId}/`
      )
      .then();
  }

  fetchVacationCarryOverRequestCommentsMentionableUsers(
    vacationCarryOverId: number
  ): Promise<IUserData[]> {
    return this.axios
      .get(`${this.endpoint}${vacationCarryOverId}/comments/mentionable-users/`)
      .then(({ data }) => data);
  }
}
